import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import MuxPlayer from "@mux/mux-player-react";
import { toast } from "sonner";
import type { ViewerCover } from "@demore/shared/types";
import { ViewerShell } from "./ViewerShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/input";
import { api } from "@/lib/api";

export function CoverPage() {
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const ref = params.get("ref") ?? undefined;
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["viewer-cover", token],
    queryFn: () => api.get<ViewerCover>(`/api/view/${token}`),
    enabled: !!token,
    retry: false,
  });

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [ndaSignName, setNdaSignName] = useState("");
  const [ndaAccepted, setNdaAccepted] = useState(false);
  // Audit V1: "Are you {recipientName}?" affirmation state.
  // null = not asked yet, "yes" = original opener, "no" = reshare claim
  const [reshareChoice, setReshareChoice] = useState<null | "yes" | "no">(null);
  // Audit V1: pre-fill email from the issuance once data lands — but never
  // once the viewer has declared they're a different person ("No, I'm someone
  // else"), or this effect would re-clobber their cleared field with the
  // original recipient's email and mis-attribute the reshare/child chain.
  useEffect(() => {
    if (reshareChoice === "no") return;
    if (data?.recipientEmail && !email) setEmail(data.recipientEmail);
  }, [data?.recipientEmail, email, reshareChoice]);

  const identify = useMutation({
    mutationFn: () =>
      api.post(`/api/view/${token}/identify`, {
        name,
        email,
        company: company || undefined,
        ref,
        identifyAsReshare: reshareChoice === "no",
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["viewer-cover", token] }); },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not identify"),
  });
  const affirmOriginal = useMutation({
    mutationFn: () => api.post(`/api/view/${token}/identify/affirm`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["viewer-cover", token] }),
  });
  const signNda = useMutation({
    mutationFn: () => api.post(`/api/view/${token}/nda`, { signatureName: ndaSignName, accepted: true }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["viewer-cover", token] }); },
    onError: (err) => toast.error(err instanceof Error ? err.message : "NDA failed"),
  });

  useEffect(() => {
    if (data && data.identified && (!data.ndaRequired || data.ndaSigned)) {
      nav(`/view/${token}/rate`);
    }
  }, [data, token, nav]);

  if (isLoading) {
    return <ViewerShell><div className="text-center py-20 text-navy-200">Loading…</div></ViewerShell>;
  }
  if (error || !data) {
    return (
      <ViewerShell>
        <div className="text-center py-16 space-y-2">
          <div className="font-head text-2xl">This link is no longer available.</div>
          <p className="text-navy-200 text-sm">It may have expired or been revoked.</p>
        </div>
      </ViewerShell>
    );
  }

  return (
    <ViewerShell>
      <div className="space-y-6">
        <section className="text-center space-y-2">
          <h1 className="font-head text-3xl md:text-4xl font-semibold leading-tight">
            Hi {data.recipientName} —
          </h1>
          <p className="text-navy-200 font-medium">
            {data.senderFirstName} prepared this just for you.
          </p>
        </section>

        {data.welcomeMessage && (
          <section className="bg-viewer-soft rounded-md p-5 text-center">
            <p className="text-white whitespace-pre-line">{data.welcomeMessage}</p>
          </section>
        )}

        {data.personalIntro && (
          <section className="bg-viewer-soft rounded-md overflow-hidden">
            <div className="aspect-video bg-navy">
              {data.personalIntro.playbackUrl ? (
                <MuxPlayer
                  src={data.personalIntro.playbackUrl}
                  poster={data.personalIntro.thumbnailUrl ?? undefined}
                  streamType="on-demand"
                  accentColor="#E6AC2A"
                  className="w-full h-full object-cover"
                />
              ) : data.personalIntro.thumbnailUrl ? (
                <img src={data.personalIntro.thumbnailUrl} alt="" className="w-full h-full object-cover opacity-90" />
              ) : null}
            </div>
            <div className="p-4 text-sm text-navy-100">A short hello from {data.senderFirstName}.</div>
          </section>
        )}

        {/* Audit V1: reshare affirmation — shown only when this link has been
            opened before by some other identity and we haven't yet asked. */}
        {!data.identified && data.reshareAffirmationNeeded && reshareChoice === null && (
          <section className="space-y-3 bg-viewer-soft rounded-md p-5">
            <div className="font-head text-xl">Are you {data.recipientName}?</div>
            <p className="text-sm text-navy-200">
              Just confirming so we keep the right notes on your account.
            </p>
            <div className="flex gap-3">
              <Button
                className="flex-1"
                onClick={() => {
                  setReshareChoice("yes");
                  affirmOriginal.mutate();
                }}
                disabled={affirmOriginal.isPending}
              >
                Yes, I'm {data.recipientName}
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setReshareChoice("no");
                  setName("");
                  setEmail("");
                  setCompany("");
                }}
              >
                No, I'm someone else
              </Button>
            </div>
          </section>
        )}

        {!data.identified && (!data.reshareAffirmationNeeded || reshareChoice !== null) && (
          <section className="space-y-3">
            <div className="font-head text-xl">
              {reshareChoice === "no"
                ? "Let us know who you are"
                : "Confirm your details"}
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div className="space-y-1.5"><Label htmlFor="name">Your name</Label><Input id="name" value={name} onChange={(e) => setName(e.target.value)} /></div>
              <div className="space-y-1.5"><Label htmlFor="email">Email</Label><Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
              <div className="space-y-1.5"><Label htmlFor="company">Company (optional)</Label><Input id="company" value={company} onChange={(e) => setCompany(e.target.value)} /></div>
            </div>
            <Button size="lg" className="w-full" disabled={!name || !email || identify.isPending} onClick={() => identify.mutate()}>
              {identify.isPending ? "Saving…" : "Continue"}
            </Button>
          </section>
        )}

        {data.identified && data.ndaRequired && !data.ndaSigned && data.ndaBody && (
          <section className="space-y-3">
            <div className="font-head text-xl">Before you watch</div>
            <div className="bg-viewer-soft rounded-md p-4 max-h-64 overflow-y-auto text-xs whitespace-pre-line">
              {data.ndaBody}
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={ndaAccepted} onChange={(e) => setNdaAccepted(e.target.checked)} className="mt-1" />
              I agree to the terms above.
            </label>
            <div className="space-y-1.5"><Label>Type your full name to sign</Label><Input value={ndaSignName} onChange={(e) => setNdaSignName(e.target.value)} /></div>
            <Button size="lg" className="w-full" disabled={!ndaAccepted || !ndaSignName || signNda.isPending} onClick={() => signNda.mutate()}>
              {signNda.isPending ? "Signing…" : "Accept & continue"}
            </Button>
          </section>
        )}
      </div>
    </ViewerShell>
  );
}
